import {
	Autocomplete,
	Box,
	Button,
	Modal,
	TextField,
} from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
	fetchClassroomsDropdown,
	fetchCoursesDropdown,
	fetchStandaloneCoursesDropdown,
	fetchBundlesDropdown,
} from '@Features/dropdowns/dropdownsSlice';
import { productOptionLabel } from '@Features/dropdowns/dropdownsSlice.types';
import { Autocomplete as EdAutocomplete } from '@Components/Autocomplete/Autocomplete';
import usePlatformMode from '@Hooks/usePlatformMode';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import { Divider } from '@Styled/utilities';
import {
	promoCodesCreate,
	promoCodesUpdate,
} from '../Slices/PromoCodes.slice';
import { FlexLayout, GridLayout } from '@eduact/ed-system';
import { CreatePromoCode, PromoCode, TargetMode } from '../Types/PromoCodes.types';
import EdFormControl, {
	TextFieldLabel,
} from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import EdBox from '@Components/UI/Utilities/EdBox/EdBox';
import { EdFormControlLabel } from '@Components/UI/Inputs/EdFormControlLabel/EdFormControlLabel';
import { DatePicker } from '@mui/x-date-pickers';
import { useRouteProvider } from '@Providers/Routes/RoutesProvider';
import { debounce, differenceBy, omit, xorBy } from 'lodash';
import { useSnackbar } from '@Providers/useSnackbar';
import { searchPromocodeStudentsRequest } from '../Services/PromoCodes.req';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import { applyBackendErrors } from '@Utils/applyBackendErrors';

type Props = {
	data?: { id: number } & PromoCode;
};

/**
 * Instructor promocode form. Percentage-only by design — there is no Type
 * select and the backend forces value_type='percentage' — and the target is a
 * single radio-picked mode over the instructor's own products ('all' = the
 * instructor_id wildcard, no product FK).
 */
const nanToNull = (value: any) => (isNaN(value) ? null : value);

const SCHEMA = yup.object({
	id: yup.number().nullable(true),
	name: yup.string().optional(),
	code: yup.string().when('id', {
		is: (id: any) => id !== undefined,
		then: (s) => s.nullable(true).max(10),
		otherwise: (s) => s.required().max(10),
	}),
	description: yup.string().nullable(true),
	max_uses: yup.number().nullable(true).transform(nanToNull),
	max_uses_per_user: yup.number().nullable(true).transform(nanToNull),
	value: yup
		.number()
		.transform(nanToNull)
		.nullable(true)
		.typeError('Discount % must be a number')
		.integer('Discount % must be a whole number')
		.min(1, 'Discount % must be between 1 and 100')
		.max(100, 'Discount % must be between 1 and 100')
		.required('Discount % is required'),
	value_cap: yup.number().nullable(true).transform(nanToNull),
	min_amount_to_apply: yup.number().nullable(true).transform(nanToNull),
	expires_at: yup
		.date()
		.nullable(true)
		.transform((value) => (value === undefined ? null : value)),
	user_restriction: yup.boolean().optional(),
	target_mode: yup
		.mixed()
		.oneOf(['classroom', 'course', 'standalone_course', 'bundle', 'all'])
		.required(),
	classroom_id: yup
		.number()
		.nullable(true)
		.transform(nanToNull)
		.when('target_mode', {
			is: (mode: TargetMode) => mode === 'classroom' || mode === 'course',
			then: (s) => s.required('Classroom is required'),
		}),
	course_id: yup
		.number()
		.nullable(true)
		.transform(nanToNull)
		.when('target_mode', {
			is: 'course',
			then: (s) => s.required('Course is required'),
		}),
	standalone_course_id: yup
		.number()
		.nullable(true)
		.transform(nanToNull)
		.when('target_mode', {
			is: 'standalone_course',
			then: (s) => s.required('Standalone course is required'),
		}),
	bundle_id: yup
		.number()
		.nullable(true)
		.transform(nanToNull)
		.when('target_mode', {
			is: 'bundle',
			then: (s) => s.required('Bundle is required'),
		}),
	restricted_to_new_users: yup.boolean().nullable(true),
	users: yup.array(),
});

/** Most specific binding first, mirroring CardTarget's resolveTargetType. */
const modeForCode = (code: PromoCode): TargetMode => {
	if (code.bundle_id) return 'bundle';
	if (code.standalone_course_id) return 'standalone_course';
	if (code.course_id) return 'course';
	if (code.classroom_id) return 'classroom';
	return 'all';
};

const PromoCodeForm = ({ data }: Props) => {
	const dispatch = useAppThunkDispatch();
	const {
		formState: { isValid, isDirty },
		getValues,
		watch,
		reset,
		control,
		handleSubmit,
		setValue,
		setError,
	} = useForm<CreatePromoCode>({
		resolver: yupResolver(SCHEMA),
		mode: 'onTouched',
		defaultValues: data
			? {
					...omit(data, 'code', 'users'),
					users: data.users ?? [],
					target_mode: modeForCode(data),
			  }
			: { expires_at: null, users: [], target_mode: 'all' },
	});

	const { dropdownClassrooms, dropdownCourses, dropdownStandaloneCourses, dropdownBundles } =
		useTypedSelector((state) => state.dropdowns);
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled } =
		usePlatformMode();

	const target_mode = watch('target_mode');
	const classroom_id = watch('classroom_id');

	const availableModes = useMemo(() => {
		const modes: Array<{ value: TargetMode; label: string }> = [];
		if (classroomsEnabled) {
			modes.push({ value: 'classroom', label: 'Classroom' });
			modes.push({ value: 'course', label: 'Course' });
		}
		if (standaloneEnabled)
			modes.push({ value: 'standalone_course', label: 'Standalone Course' });
		if (bundlesEnabled) modes.push({ value: 'bundle', label: 'Bundle' });
		modes.push({ value: 'all', label: 'All my products' });
		return modes;
	}, [classroomsEnabled, standaloneEnabled, bundlesEnabled]);

	useEffect(() => {
		dispatch(fetchClassroomsDropdown());
	}, [dispatch]);

	useEffect(() => {
		if (standaloneEnabled) dispatch(fetchStandaloneCoursesDropdown());
		if (bundlesEnabled) dispatch(fetchBundlesDropdown());
	}, [standaloneEnabled, bundlesEnabled, dispatch]);

	// Courses load per classroom, like the admin modal.
	useEffect(() => {
		if (classroom_id) dispatch(fetchCoursesDropdown(classroom_id));
	}, [classroom_id, dispatch]);

	// Switching mode invalidates every picked target; the ref skips the mount
	// pass so an edited promocode keeps its prefilled target.
	const prevModeRef = useRef(target_mode);
	useEffect(() => {
		if (prevModeRef.current !== target_mode) {
			setValue('classroom_id', null as any);
			setValue('course_id', null as any);
			setValue('standalone_course_id', null as any);
			setValue('bundle_id', null as any);
		}
		prevModeRef.current = target_mode;
	}, [target_mode, setValue]);

	// A different classroom invalidates the course.
	const prevClassroomRef = useRef(classroom_id);
	useEffect(() => {
		if (prevClassroomRef.current !== classroom_id) {
			setValue('course_id', null as any);
		}
		prevClassroomRef.current = classroom_id;
	}, [classroom_id, setValue]);

	// The Autocomplete wrapper reports the picked id through target.value.
	const pickId = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value as unknown as string | number | null;
		return raw === null || raw === '' ? null : Number(raw);
	};

	const { displaySnackbar } = useSnackbar();
	const onSubmitForm = async (formData: CreatePromoCode) => {
		const mode = formData.target_mode;
		// Always carry all four target keys (null for unselected): the backend
		// merges the patch over the row, so switching target must clear the old
		// FK explicitly.
		const payload = {
			...omit(formData, ['target_mode']),
			classroom_id:
				mode === 'classroom' || mode === 'course'
					? formData.classroom_id ?? null
					: null,
			course_id: mode === 'course' ? formData.course_id ?? null : null,
			standalone_course_id:
				mode === 'standalone_course'
					? formData.standalone_course_id ?? null
					: null,
			bundle_id: mode === 'bundle' ? formData.bundle_id ?? null : null,
			users: formData.user_restriction
				? formData.users.map((user) => user.id)
				: [],
		};
		try {
			if (data) {
				const resp = await dispatch(promoCodesUpdate(payload as any)).unwrap();
				displaySnackbar('success', resp.message);
			} else {
				const resp = await dispatch(promoCodesCreate(payload as any)).unwrap();
				displaySnackbar('success', resp.message);
			}
			onClose();
		} catch (error) {
			const applied = applyBackendErrors(error, setError as any);
			if (!applied) {
				displaySnackbar(
					'error',
					error?.response?.data?.message || 'Something went wrong'
				);
			}
		}
	};

	const parent = useRouteProvider();
	const onClose = () => {
		parent?.goToParent();
	};

	const students = watch('users');

	const [openAutoComplete, setOpenAutoComplete] = useState(false);
	const [query, setQuery] = useState('');
	const handleAutoComplete = async (value: string) => {
		if (value.length < 3) {
			setOptions([]);
			return;
		}
		try {
			setLoading(true);
			const { data: resp } = await searchPromocodeStudentsRequest(value);
			const mapped = resp.data.map((_) => ({
				id: _.id,
				username: _.username,
				label: `${[_.first_name, _.last_name].filter(Boolean).join(' ')} (${
					_.username
				})`,
			}));
			// differenceBy, not xorBy: the symmetric difference used to inject
			// already-picked students back into the dropdown.
			setOptions(differenceBy(mapped, getValues().users, (_) => _.id));
		} catch (error) {
			displaySnackbar('error', 'Student search failed');
			setOptions([]);
		} finally {
			setLoading(false);
		}
	};
	const debounceFn = useCallback(debounce(handleAutoComplete, 1000), []);

	const handleQuery = (value: string) => {
		setQuery(value);
		debounceFn(value);
	};
	const [options, setOptions] = useState<
		Array<{ id: number; username: string; label?: string }>
	>([]);

	// A real in-flight flag: the old open-and-empty derivation rendered
	// "Loading..." forever for empty results and failed requests alike.
	const [loading, setLoading] = useState(false);

	const user_restriction = watch('user_restriction');

	return (
		<>
			<Modal
				open={true}
				onClose={onClose}
				sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<FlexLayout>
					<EdBox
						minWidth={'60vw'}
						minHeight={'60vh'}
						bgcolor={'white'}
						width="fit-content"
						maxWidth={'75vw'}
						height="fit-content"
					>
						<form onSubmit={handleSubmit(onSubmitForm)}>
							<FlexLayout mb="1rem">
								<TitleStyled id="modal-modal-title">
									{data ? 'Edit Promo Code' : 'Create Promo Code'}
								</TitleStyled>
							</FlexLayout>
							<Divider label="Info" />
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								<EdFormControl control={control} name="name" label={'Name'} />
								{!data && (
									<EdFormControl
										required
										inputProps={{ maxLength: 10 }}
										control={control}
										name="code"
										label={'Code'}
									/>
								)}
								<Controller
									control={control}
									name="value"
									render={({ field, fieldState }) => (
										<FlexLayout flexDirection="column">
											<FlexLayout>
												<TextFieldLabel required>Discount %</TextFieldLabel>
												<EdNumericInput size="small" fullWidth {...field} />
											</FlexLayout>
											{fieldState.error && (
												<ErrorText>{fieldState.error.message}</ErrorText>
											)}
										</FlexLayout>
									)}
								/>
								<Controller
									control={control}
									name="value_cap"
									render={({ field: { ref, ...field } }) => (
										<FlexLayout>
											<EdFormControlLabel>Value Cap</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								<EdFormControl
									control={control}
									name="description"
									label={'Description'}
									multiline
									minRows={2}
								/>
							</GridLayout>
							<Divider label="Usage" />
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								<Controller
									control={control}
									name="max_uses"
									render={({ field: { ref, ...field } }) => (
										<FlexLayout>
											<EdFormControlLabel>Max usage</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								<Controller
									control={control}
									name="max_uses_per_user"
									render={({ field: { ref, ...field } }) => (
										<FlexLayout>
											<EdFormControlLabel>
												Max usage per user
											</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								<Controller
									control={control}
									name="min_amount_to_apply"
									render={({ field: { ref, ...field } }) => (
										<FlexLayout>
											<EdFormControlLabel>Min Value</EdFormControlLabel>
											<EdNumericInput size="small" fullWidth {...field} />
										</FlexLayout>
									)}
								/>
								<Controller
									control={control}
									name="expires_at"
									render={({ field: { onChange, value, ...field } }) => {
										const _value = value ?? null;
										return (
											<FlexLayout>
												<EdFormControlLabel>Expires At</EdFormControlLabel>
												<DatePicker
													{...field}
													value={_value}
													onChange={(v) => onChange(v)}
													renderInput={(params) => (
														<TextField {...params} size="small" />
													)}
												/>
											</FlexLayout>
										);
									}}
								/>
							</GridLayout>
							<Divider label="Target" />
							<Controller
								control={control}
								name="target_mode"
								render={({ field }) => (
									<RadioGroupStyled
										{...field}
										onChange={(e) => field.onChange(e.target.value)}
									>
										{availableModes.map((mode) => (
											<FormControlLabel
												key={mode.value}
												value={mode.value}
												control={<Radio />}
												label={mode.label}
											/>
										))}
									</RadioGroupStyled>
								)}
							/>
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								{(target_mode === 'classroom' || target_mode === 'course') && (
									<Controller
										control={control}
										name="classroom_id"
										render={({ field: { onChange, ...others }, fieldState }) => (
											<EdAutocomplete
												options={dropdownClassrooms.map((classroom) => ({
													id: classroom.id,
													label: `${classroom.title} (${classroom.label})`,
												}))}
												{...others}
												fieldState={fieldState}
												label="Classroom"
												onChange={(e) =>
													setValue('classroom_id', pickId(e) as any, {
														shouldDirty: true,
														shouldValidate: true,
													})
												}
											/>
										)}
									/>
								)}
								{target_mode === 'course' && (
									<Controller
										control={control}
										name="course_id"
										render={({ field: { onChange, ...others }, fieldState }) => (
											<EdAutocomplete
												options={dropdownCourses.map((course) => ({
													id: course.id,
													label: course.name,
												}))}
												{...others}
												fieldState={fieldState}
												label="Course"
												disabled={!classroom_id}
												onChange={(e) =>
													setValue('course_id', pickId(e) as any, {
														shouldDirty: true,
														shouldValidate: true,
													})
												}
											/>
										)}
									/>
								)}
								{target_mode === 'standalone_course' && (
									<EdFormControl
										control={control}
										name="standalone_course_id"
										label="Standalone Course"
										select
									>
										{dropdownStandaloneCourses.map((course) => (
											<MenuItem key={course.id} value={course.id}>
												{productOptionLabel(course)}
											</MenuItem>
										))}
									</EdFormControl>
								)}
								{target_mode === 'bundle' && (
									<EdFormControl
										control={control}
										name="bundle_id"
										label="Bundle"
										select
									>
										{dropdownBundles.map((bundle) => (
											<MenuItem key={bundle.id} value={bundle.id}>
												{productOptionLabel(bundle)}
											</MenuItem>
										))}
									</EdFormControl>
								)}
							</GridLayout>
							<Divider label="Restrictions" />
							<GridLayout
								gridGap={'1.5rem'}
								gridTemplateColumns={{
									sm: 'repeat(1,1fr)',
									md: 'repeat(2,1fr)',
									lg: 'repeat(3,1fr)',
								}}
							>
								<EdFormControl
									control={control}
									name="user_restriction"
									label={'User restriction'}
									switch
								/>
								{user_restriction && (
									<Controller
										control={control}
										name="users"
										render={({ field }) => (
											<FlexLayout flexDirection={'column'} width={'100%'}>
												<FlexLayout>
													<EdFormControlLabel>Students</EdFormControlLabel>
													<Autocomplete
														size="small"
														fullWidth
														multiple
														open={openAutoComplete}
														onOpen={() => setOpenAutoComplete(true)}
														onClose={() => setOpenAutoComplete(false)}
														options={options}
														// The server already filtered (username OR phone).
														// MUI's default filter would re-filter against the
														// label and hide every phone-number match.
														filterOptions={(results) => results}
														noOptionsText="No students found"
														getOptionLabel={(option: {
															id: number;
															username: string;
															label?: string;
														}) => option.label ?? option.username}
														loading={loading}
														onChange={(e, value) => {
															field.onChange(value);
														}}
														onInputChange={(e, v) => handleQuery(v)}
														inputValue={query}
														value={field.value}
														renderTags={() => <></>}
														renderInput={({ ...params }) => (
															<TextField {...params} />
														)}
													/>
												</FlexLayout>

												<FlexLayout flexWrap={'wrap'}>
													{students.map((student) => {
														return (
															<SelectChip
																key={student.id}
																onDelete={() =>
																	setValue(
																		'users',
																		xorBy(students, [student], (_) => _.id)
																	)
																}
																label={student.username}
															/>
														);
													})}
												</FlexLayout>
											</FlexLayout>
										)}
									/>
								)}
								<EdFormControl
									control={control}
									name="restricted_to_new_users"
									label={'New users restriction'}
									switch
								/>
							</GridLayout>
							<ButtonsContainer>
								<Button
									variant="text"
									color="inherit"
									sx={{
										fontFamily: 'Roboto',
										fontSize: '14px',
										padding: '6px 16px',
										color: '#6c63ff',
										backgroundColor: '#fff',
										outline: 'none',
									}}
									disabled={!isValid || !isDirty}
									type="submit"
								>
									Submit
								</Button>
								<Button
									variant="text"
									color="warning"
									sx={{
										fontFamily: 'Roboto',
										fontSize: '14px',
										padding: '6px 16px',
										color: '#ff9800',
										outline: 'none',
										backgroundColor: '#fff',
									}}
									disabled={!isDirty}
									onClick={() => {
										reset();
									}}
								>
									Cancel
								</Button>
							</ButtonsContainer>
						</form>
					</EdBox>
				</FlexLayout>
			</Modal>
		</>
	);
};

export default PromoCodeForm;

export const TitleStyled = styled.span`
	font-weight: 500;
	font-family: Roboto;
	text-align: left;
	font-size: 1rem;
	color: rgba(0, 0, 0, 0.6);
`;

export const ButtonsContainer = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 0.5rem;
	outline: none;
	margin-top: 1.625rem;
`;

export const RadioGroupStyled = styled(RadioGroup)`
	display: flex;
	flex-direction: row;
	margin-bottom: 1rem;
	.MuiTypography-root {
		font-family: Roboto;
		font-size: 0.875rem;
		color: rgba(0, 0, 0, 0.87);
	}
	.MuiRadio-root.Mui-checked {
		color: #6c63ff;
	}
`;

const ErrorText = styled.span`
	color: #d32f2f;
	font-size: 0.75rem;
	font-family: Roboto;
	margin-top: 0.25rem;
`;
